import { useTranslation } from 'react-i18next';
import { HOURLY_AREA_PRICING, areaPricingProblem, type QuoteAreaPricing } from '@shared/area-pricing.ts';
import { areaLaborCents, formatCents } from '@shared/calculations/pricing.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import type { Quote } from '@shared/types/index.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Select } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { NumberInput } from '../number-input.tsx';

export function QuoteAreaPricingCard({
  quote,
  update,
  readOnly,
}: {
  quote: Quote;
  update: (mutate: (draft: Quote) => Quote) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { company } = useSession();
  const area = quote.areaPricing ?? HOURLY_AREA_PRICING;
  const rules = company?.currencyCode === quote.currencyCode ? company.areaPricingRules : null;
  const configured = area.minRateCents !== null || !!rules;
  // Room prices do not block square-foot pricing: residential room types
  // usually carry one, and choosing square feet clears them (see
  // withoutAreaRoomPrices in the editor). Groups and tax-free tasks still do.
  const incompatible =
    (quote.groups?.length ?? 0) > 1 ||
    quote.groups?.some((group) => group.quantity !== 1) ||
    quote.rooms.some((room) => room.services.some((task) => task.isSelected && !task.isTaxable));
  const hasRoomPrices = quote.rooms.some((room) => room.flatPriceCents != null);
  function patch(values: Partial<QuoteAreaPricing>) {
    update((draft) => ({
      ...draft,
      areaPricing: {
        ...(draft.areaPricing ?? HOURLY_AREA_PRICING),
        ...(draft.areaPricing?.minRateCents == null && rules
          ? {
              minRateCents: rules.minRateCents,
              maxRateCents: rules.maxRateCents,
              rateCents: rules.defaultRateCents,
            }
          : {}),
        ...values,
      },
    }));
  }
  const money = (cents: number, currencyCode: string) => formatCents(cents, currencyCode, getDateLocale());
  const summary = (() => {
    if (area.mode !== 'area' || area.squareFeet === null || area.rateCents === null) return null;
    const perVisitCents = areaLaborCents({ squareFeet: area.squareFeet, rateCents: area.rateCents }, {});
    return {
      perVisit: money(perVisitCents, quote.currencyCode),
      monthly:
        quote.billingBasis === 'monthly'
          ? money(
              areaLaborCents(
                { squareFeet: area.squareFeet, rateCents: area.rateCents },
                { billingBasis: 'monthly', visitsPerWeek: quote.visitsPerWeek },
              ),
              quote.currencyCode,
            )
          : null,
    };
  })();
  const problem = areaPricingProblem(area, quote.billingBasis === 'monthly', quote.currencyCode);
  return (
    <section className="card" aria-labelledby="quote-area-heading">
      <h2 id="quote-area-heading" className="mb-3 text-lg font-semibold">
        {t('areaPricing.heading')}
      </h2>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('areaPricing.quoteHint')}</p>
      {!area.confirmed && <Alert tone="warning">{t('areaPricing.confirmMode')}</Alert>}
      <Select
        label={t('areaPricing.mode')}
        value={area.confirmed ? area.mode : ''}
        disabled={readOnly}
        options={[
          { value: '', label: t('areaPricing.choose') },
          { value: 'hourly', label: t('areaPricing.hourly') },
          ...(!configured || incompatible ? [] : [{ value: 'area', label: t('areaPricing.area') }]),
        ]}
        onChange={(event) => {
          if (event.target.value === 'hourly' || event.target.value === 'area')
            patch({ mode: event.target.value, confirmed: true });
        }}
      />
      {configured && !incompatible && hasRoomPrices && !(area.confirmed && area.mode === 'area') && (
        <p className="mt-1 text-sm text-[color:var(--color-muted)]">{t('areaPricing.roomPricesReplaced')}</p>
      )}
      {area.mode === 'area' && (
        <div className="grid gap-x-4 sm:grid-cols-2">
          <NumberInput
            label={t('areaPricing.squareFeet')}
            value={area.squareFeet}
            min={1}
            max={1_000_000}
            disabled={readOnly}
            onValueChange={(squareFeet) => patch({ squareFeet })}
          />
          <MoneyInput
            label={t('areaPricing.rate')}
            currencyCode={quote.currencyCode}
            valueCents={area.rateCents}
            min={0}
            disabled={readOnly}
            onChangeCents={(rateCents) => patch({ rateCents })}
          />
        </div>
      )}
      {area.mode === 'area' && (
        <div className="mt-2">
          {summary && (
            <ul className="mt-2 text-sm" aria-live="polite">
              <li>{t('areaPricing.perVisitPrice', { amount: summary.perVisit })}</li>
              {summary.monthly && <li>{t('areaPricing.monthlyPrice', { amount: summary.monthly })}</li>}
            </ul>
          )}
        </div>
      )}
      {problem && <Alert tone="warning">{t('areaPricing.invalid')}</Alert>}
      {(!configured || incompatible) && (
        <p className="text-sm text-[color:var(--color-muted)]">{t('areaPricing.unavailable')}</p>
      )}
    </section>
  );
}
